import type { WorkingDay } from '../api/types';
import { formatMinutes, weekdayRu, type TimeFormat } from './time';

/**
 * Правила карточки организации: маски, проверки и сводки. Держим отдельно от
 * экрана — одни и те же проверки нужны и на `blur`, и перед отправкой, и
 * подэкранам, а разъехавшиеся копии правил дают форму, которая ругается
 * по-разному в зависимости от того, как в неё вошли.
 */

// --- телефон ---------------------------------------------------------------

/**
 * Маска `+7 (999) 999-99-99`. Ведём по цифрам, а не по позиции курсора: на
 * мобильной клавиатуре ввод приходит кусками, и посимвольный автомат сыплется
 * на первой же вставке из буфера.
 */
export function maskPhone(raw: string): string {
  let digits = raw.replace(/\D/g, '');
  if (!digits) return '';
  // 8 в начале — привычная запись того же номера; 9 — набрали без кода страны.
  if (digits[0] === '8') digits = `7${digits.slice(1)}`;
  else if (digits[0] === '9') digits = `7${digits}`;
  else if (digits[0] !== '7') digits = `7${digits}`;
  digits = digits.slice(0, 11);

  const rest = digits.slice(1);
  let out = '+7';
  if (rest.length) out += ` (${rest.slice(0, 3)}`;
  if (rest.length > 3) out += `) ${rest.slice(3, 6)}`;
  if (rest.length > 6) out += `-${rest.slice(6, 8)}`;
  if (rest.length > 8) out += `-${rest.slice(8, 10)}`;
  return out;
}

const phoneDone = (v: string) => v.replace(/\D/g, '').length === 11;

// --- сайт и ник ------------------------------------------------------------

/** `logos.ru` → `https://logos.ru`. Схему дописываем молча: её забывают все. */
export function normalizeSite(raw: string): string {
  const v = raw.trim();
  if (!v) return '';
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
}

export const TG_RE = /^@?[A-Za-z0-9_]{5,32}$/;

/** `ivanova` → `@ivanova`. Собаку дописываем — в сообщения уходит именно ник. */
export function normalizeTg(raw: string): string {
  const v = raw.trim().replace(/^(https?:\/\/)?(t\.me\/)/i, '');
  if (!v) return '';
  return v.startsWith('@') ? v : `@${v}`;
}

// --- проверки полей --------------------------------------------------------

/**
 * Поля карточки организации. Ника администратора здесь нет намеренно: контакт
 * для клиентов берётся из реестра администраторов, где его правит сам человек.
 * Вторая копия рядом с телефоном организации расходилась бы с первой.
 */
export type OrgForm = {
  title: string;
  about: string;
  phone: string;
  email: string;
  site: string;
  tz: string;
};

export type FieldName = keyof OrgForm;

/**
 * Поля формы списком. Черновик экрана шире неё — там же логотип и числовые
 * правила, — и обходить его ключами нельзя: проверки работают со строками.
 */
export const FIELDS: FieldName[] = ['title', 'about', 'phone', 'email', 'site', 'tz'];

/** Ошибка поля или null. Пустое необязательное поле — всегда валидно. */
export function fieldError(name: FieldName, value: string): string | null {
  const v = value.trim();
  switch (name) {
    case 'title':
      if (!v) return 'Без названия клиент не поймёт, куда записывается';
      if (v.length < 2) return 'Слишком короткое название';
      if (v.length > 60) return 'Не длиннее 60 символов';
      return null;
    case 'about':
      return v.length > 120 ? 'Не длиннее 120 символов' : null;
    case 'phone':
      return !v || phoneDone(v) ? null : 'Номер не дописан';
    case 'email':
      if (!v) return null;
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? null : 'Похоже на опечатку — нужен адрес вида name@domain.ru';
    case 'site':
      if (!v) return null;
      return /^https?:\/\/[^\s.]+\.[^\s]+$/i.test(normalizeSite(v)) ? null : 'Похоже на опечатку в адресе сайта';
    case 'tz':
      return v ? null : 'Без часового пояса не посчитать расписание';
    default:
      return null;
  }
}

/** Все ошибки формы разом — для отправки и для скролла к первому проблемному полю. */
export function formErrors(form: OrgForm): Partial<Record<FieldName, string>> {
  const out: Partial<Record<FieldName, string>> = {};
  for (const name of FIELDS) {
    const err = fieldError(name, form[name]);
    if (err) out[name] = err;
  }
  return out;
}

// --- часы работы -----------------------------------------------------------

export const DEFAULT_HOURS: WorkingDay[] = [0, 1, 2, 3, 4, 5, 6].map((d) => ({
  d, on: d < 5 ? 1 : 0, from: 9 * 60, to: 20 * 60,
}));

/**
 * «пн–пт 09:00–20:00», «пн–пт 09:00–20:00, сб 10:00–16:00», «круглосуточно».
 * Подряд идущие дни с одинаковым временем склеиваем: семь строк в сводку не
 * влезают, а «пн, вт, ср, чт, пт» читается хуже, чем «пн–пт».
 */
export function hoursSummary(hours: WorkingDay[], tf: TimeFormat): string {
  const open = hours.filter((h) => h.on);
  if (!open.length) return 'выходные все дни';

  const groups: { from: number; to: number; days: number[] }[] = [];
  for (const h of [...open].sort((a, b) => a.d - b.d)) {
    const last = groups[groups.length - 1];
    if (last && last.from === h.from && last.to === h.to && last.days[last.days.length - 1] === h.d - 1) {
      last.days.push(h.d);
    } else {
      groups.push({ from: h.from, to: h.to, days: [h.d] });
    }
  }

  return groups
    .map((g) => {
      const days = g.days.length > 2
        ? `${weekdayRu(g.days[0])}–${weekdayRu(g.days[g.days.length - 1])}`
        : g.days.map((d) => weekdayRu(d)).join(', ');
      return `${days} ${formatMinutes(g.from, tf)}–${formatMinutes(g.to, tf)}`;
    })
    .join(', ');
}

/** Совпадают ли два набора часов — по нему кнопка «Сохранить» решает, есть ли правки. */
export const sameHours = (a: WorkingDay[], b: WorkingDay[]): boolean =>
  a.length === b.length && a.every((h, i) => h.d === b[i].d && !!h.on === !!b[i].on && h.from === b[i].from && h.to === b[i].to);
